import Keycloak from "keycloak-js"
import { clearSession } from "@/auth/session"

// Browsers only expose crypto.randomUUID on secure origins (HTTPS/localhost).
// front.test over HTTP is not secure, so polyfill it for local development.
if (process.env.NODE_ENV === "development" && typeof crypto !== "undefined" && !crypto.randomUUID) {
  crypto.randomUUID = () =>
    "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (c) =>
      (+c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))).toString(16),
    )
}

// Single Keycloak instance for the whole app. The access/refresh tokens live
// in-memory on this instance and are never persisted to storage.
const keycloak = new Keycloak({
  url: process.env.VUE_APP_KEYCLOAK_URL,
  realm: process.env.VUE_APP_KEYCLOAK_REALM,
  clientId: process.env.VUE_APP_KEYCLOAK_CLIENT_ID,
})

// Current access token, or undefined before authentication completes.
export const getToken = () => keycloak.token

export const isAuthenticated = () => Boolean(keycloak.authenticated)

export const login = (options) => keycloak.login(options)

// Force a token refresh regardless of remaining validity (used by the API
// client to recover from a 401 before giving up).
export const forceRefresh = () => keycloak.updateToken(-1)

// Clear local identity/credentials, then redirect to the Keycloak logout
// endpoint. Use this everywhere instead of calling keycloak.logout() directly
// so storage cleanup happens on every logout path.
export const logout = (options = {}) => {
  clearSession()

  return keycloak.logout({ redirectUri: process.env.VUE_APP_BASE_URL, ...options })
}

// Wire the lifecycle callbacks and start Keycloak. `onReady(authenticated)` is
// the app-specific boot handler (fetch user, mount Vue) and runs once init
// resolves. Returns the init() promise so the caller can handle init failure.
export const initAuth = (onReady) => {
  keycloak.onReady = onReady

  keycloak.onAuthSuccess = () => {
    window.auth = keycloak
  }

  keycloak.onAuthRefreshSuccess = () => {
    window.auth = keycloak
  }

  keycloak.onAuthError = () => logout()

  keycloak.onAuthRefreshError = () => logout()

  keycloak.onAuthLogout = () => clearSession()

  keycloak.onTokenExpired = async () => {
    try {
      const refreshed = await keycloak.updateToken(60)

      if (!refreshed) {
        await logout()
      }
    } catch (e) {
      console.error("[AUTH] Token refresh failed", e)
      await logout()
    }
  }

  return keycloak.init({
    onLoad: "login-required",
    checkLoginIframe: false,
    ...(process.env.NODE_ENV === "development" ? { pkceMethod: false } : {}),
  })
}

export default keycloak
